// Full reservation detail — all admin actions live here.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Save, Send, Printer, DollarSign, RefreshCcw, X,
    CheckCircle2, StickyNote, Trash2, User, Mail, Phone, MapPin
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, useAdminAuth } from "@/lib/adminAuth";

const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (s) => {
    if (!s) return "—";
    try {
        const d = new Date(s + (s?.length === 10 ? "T00:00:00" : ""));
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return s; }
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-US") : "—");

const STATUS_OPTIONS = ["pending", "confirmed", "checked_in", "checked_out", "cancelled", "no_show"];
const STATUS_STYLE = {
    pending: "bg-amber-100 text-amber-800", confirmed: "bg-jade/15 text-jade",
    checked_in: "bg-emerald-100 text-emerald-700", checked_out: "bg-gray-100 text-gray-600",
    cancelled: "bg-red-100 text-red-600", no_show: "bg-red-50 text-red-500",
};

function Modal({ open, onClose, title, children }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-[#0b1f22]/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-[#0b1f22]/10 max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-[#0b1f22]/40 hover:text-[#0b1f22]"><X size={18} /></button>
                <h2 className="font-display text-2xl text-[#0b1f22]">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">{label}</span>
            {children}
        </label>
    );
}
const inputCls = "mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold bg-white";

export default function AdminReservationDetail() {
    const { id } = useParams();
    const { isSuperAdmin } = useAdminAuth();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [refundOpen, setRefundOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [noteText, setNoteText] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get(`/admin/reservations/${id}`);
            setDoc(data);
            setForm({
                guest_name: data.guest_name || "", email: data.email || "", phone: data.phone || "",
                country: data.country || "", address: data.address || "",
                adults: data.adults ?? 2, children: data.children ?? 0,
                check_in: data.check_in || "", check_out: data.check_out || "",
                room_slug: data.room_slug || "", room_quantity: data.room_quantity ?? 1,
                rate_kind: data.rate_kind || "flexible",
                subtotal: data.subtotal ?? 0, discount_amount: data.discount_amount ?? 0,
                discount_reason: data.discount_reason || "", taxes_fees: data.taxes_fees ?? 0,
                total: data.total ?? 0, special_requests: data.special_requests || "",
            });
        } catch (err) {
            toast.error(formatApiError(err));
            navigate("/admin/reservations", { replace: true });
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const saveEdits = async () => {
        setSaving(true);
        try {
            const num = (v) => (v === "" || v == null ? 0 : Number(v));
            const patch = {
                ...form,
                adults: num(form.adults), children: num(form.children), room_quantity: num(form.room_quantity),
                subtotal: num(form.subtotal), discount_amount: num(form.discount_amount),
                taxes_fees: num(form.taxes_fees), total: num(form.total),
            };
            const { data } = await adminApi.patch(`/admin/reservations/${id}`, patch);
            setDoc(data);
            toast.success("Reservation updated");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const changeStatus = async (status, reason) => {
        try {
            const { data } = await adminApi.post(`/admin/reservations/${id}/status`, { status, reason });
            setDoc(data);
            toast.success(`Status → ${status.replace("_", " ")}`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        try {
            await adminApi.post(`/admin/reservations/${id}/notes`, { note: noteText.trim() });
            setNoteText("");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const resend = async () => {
        try {
            await adminApi.post(`/admin/reservations/${id}/resend-confirmation`);
            toast.success("Confirmation email resent");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async () => {
        if (!window.confirm("Permanently delete this reservation? This cannot be undone.")) return;
        try {
            await adminApi.delete(`/admin/reservations/${id}`);
            toast.success("Reservation deleted");
            navigate("/admin/reservations", { replace: true });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (loading || !doc) {
        return <div className="text-sm text-[#0b1f22]/50">Loading reservation…</div>;
    }

    return (
        <div className="pb-16">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-3 items-center justify-between mb-6 print:hidden">
                <Link to="/admin/reservations" className="text-[11px] uppercase tracking-[0.2em] text-[#0b1f22]/60 hover:text-gold inline-flex items-center gap-1">
                    <ArrowLeft size={12} /> Back to reservations
                </Link>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => window.print()} data-testid="print-reservation" className="border border-[#0b1f22]/20 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#0b1f22] hover:border-gold hover:text-gold inline-flex items-center gap-1"><Printer size={12} /> Print</button>
                    <button onClick={resend} data-testid="resend-confirmation" className="border border-[#0b1f22]/20 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#0b1f22] hover:border-gold hover:text-gold inline-flex items-center gap-1"><Send size={12} /> Resend confirmation</button>
                    {isSuperAdmin && <button onClick={remove} className="border border-red-300 text-red-600 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>}
                </div>
            </div>

            {/* Header */}
            <header className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Reservation</div>
                <div className="flex flex-wrap items-baseline gap-4 mt-2">
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] font-mono">{doc.confirmation_number}</h1>
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${STATUS_STYLE[doc.status] || "bg-gray-100 text-gray-600"}`}>{String(doc.status || "").replace("_", " ")}</span>
                </div>
                <p className="text-[#0b1f22]/60 text-sm mt-2">{doc.guest_name} · {fmtDate(doc.check_in)} → {fmtDate(doc.check_out)} · {doc.nights} nights · Source: {doc.source}</p>
            </header>

            {/* Money summary */}
            <section className="grid md:grid-cols-4 gap-3 mb-8">
                {[
                    { k: "Total", v: usd(doc.total) },
                    { k: "Paid", v: usd(doc.amount_paid) },
                    { k: "Refunded", v: usd(doc.amount_refunded) },
                    { k: "Balance", v: usd(doc.remaining_balance), highlight: Number(doc.remaining_balance) > 0 },
                ].map((k) => (
                    <div key={k.k} className="bg-white border border-[#0b1f22]/10 rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#0b1f22]/55">{k.k}</div>
                        <div className={`font-display text-2xl mt-1 ${k.highlight ? "text-amber-700" : "text-[#0b1f22]"}`}>{k.v}</div>
                    </div>
                ))}
            </section>

            {/* Action row */}
            <section className="flex flex-wrap gap-2 mb-8 print:hidden">
                <select value={doc.status} onChange={(e) => changeStatus(e.target.value)} data-testid="status-select"
                    className="border border-[#0b1f22]/15 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] bg-white text-[#0b1f22]">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <button onClick={() => setPayOpen(true)} data-testid="record-payment" className="bg-jade/90 text-white rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-jade inline-flex items-center gap-1"><DollarSign size={12} /> Record payment</button>
                <button onClick={() => setRefundOpen(true)} data-testid="record-refund" className="border border-[#0b1f22]/20 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#0b1f22] hover:border-gold hover:text-gold inline-flex items-center gap-1"><RefreshCcw size={12} /> Refund</button>
                {doc.status !== "cancelled" && (
                    <button onClick={() => setCancelOpen(true)} data-testid="cancel-reservation" className="border border-red-300 text-red-600 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-red-50 inline-flex items-center gap-1"><X size={12} /> Cancel</button>
                )}
            </section>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left column — edit form */}
                <div className="lg:col-span-2 bg-white border border-[#0b1f22]/10 rounded-2xl p-6">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-4">Guest & booking</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Full name" testid="edit-guest-name"><input className={inputCls} value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} /></Field>
                        <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                        <Field label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                        <Field label="Country"><input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
                        <Field label="Adults"><input type="number" min="1" className={inputCls} value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} /></Field>
                        <Field label="Children"><input type="number" min="0" className={inputCls} value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} /></Field>
                        <Field label="Check-in"><input type="date" className={inputCls} value={form.check_in?.slice(0,10)} onChange={(e) => setForm({ ...form, check_in: e.target.value })} /></Field>
                        <Field label="Check-out"><input type="date" className={inputCls} value={form.check_out?.slice(0,10)} onChange={(e) => setForm({ ...form, check_out: e.target.value })} /></Field>
                        <Field label="Room slug"><input className={inputCls} value={form.room_slug} onChange={(e) => setForm({ ...form, room_slug: e.target.value })} /></Field>
                        <Field label="Room quantity"><input type="number" min="1" className={inputCls} value={form.room_quantity} onChange={(e) => setForm({ ...form, room_quantity: e.target.value })} /></Field>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mt-6 mb-4">Amounts (USD)</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Subtotal"><input type="number" step="0.01" className={inputCls} value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} /></Field>
                        <Field label="Discount"><input type="number" step="0.01" className={inputCls} value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} /></Field>
                        <Field label="Discount reason"><input className={inputCls} value={form.discount_reason} onChange={(e) => setForm({ ...form, discount_reason: e.target.value })} /></Field>
                        <Field label="Taxes & fees"><input type="number" step="0.01" className={inputCls} value={form.taxes_fees} onChange={(e) => setForm({ ...form, taxes_fees: e.target.value })} /></Field>
                        <Field label="Total"><input type="number" step="0.01" className={inputCls} value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} /></Field>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mt-6 mb-4">Special requests</div>
                    <textarea rows={3} className={inputCls} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} />
                    <button onClick={saveEdits} disabled={saving} data-testid="save-reservation" className="mt-5 bg-[#0b1f22] text-ivory rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0b1f22]/90 inline-flex items-center gap-2 disabled:opacity-60">
                        <Save size={12} /> {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>

                {/* Right column — payments, refunds, notes */}
                <div className="space-y-4">
                    <div className="bg-white border border-[#0b1f22]/10 rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-4">Payments</div>
                        {doc.payments?.length ? (
                            <ul className="space-y-3">
                                {doc.payments.map((p) => (
                                    <li key={p.id} className="border-b border-[#0b1f22]/5 pb-3 last:border-b-0 last:pb-0">
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-medium">{usd(p.amount)}</div>
                                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#0b1f22]/60">{p.method}</div>
                                        </div>
                                        <div className="text-[11px] text-[#0b1f22]/55 mt-1">{fmtDateTime(p.received_at)} · {p.reference || "no ref"}</div>
                                        {p.note && <div className="text-[11px] text-[#0b1f22]/55 mt-1 italic">"{p.note}"</div>}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-[12px] text-[#0b1f22]/50">No payments recorded yet.</div>
                        )}
                    </div>

                    <div className="bg-white border border-[#0b1f22]/10 rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-4">Refunds</div>
                        {doc.refunds?.length ? (
                            <ul className="space-y-3">
                                {doc.refunds.map((r) => (
                                    <li key={r.id} className="border-b border-[#0b1f22]/5 pb-3 last:border-b-0 last:pb-0">
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-medium text-red-600">-{usd(r.amount)}</div>
                                            <div className="text-[10px] text-[#0b1f22]/50">{fmtDateTime(r.issued_at)}</div>
                                        </div>
                                        {r.reason && <div className="text-[11px] text-[#0b1f22]/55 mt-1 italic">"{r.reason}"</div>}
                                    </li>
                                ))}
                            </ul>
                        ) : (<div className="text-[12px] text-[#0b1f22]/50">No refunds.</div>)}
                    </div>

                    <div className="bg-white border border-[#0b1f22]/10 rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-4 flex items-center gap-2"><StickyNote size={13} /> Staff notes</div>
                        {doc.notes?.length ? (
                            <ul className="space-y-3 mb-4">
                                {doc.notes.map((n) => (
                                    <li key={n.id} className="bg-[#0b1f22]/[0.03] rounded-lg p-3">
                                        <div className="text-[11px] text-[#0b1f22]/55">{fmtDateTime(n.at)} · {n.by}</div>
                                        <div className="text-sm text-[#0b1f22] mt-1 whitespace-pre-wrap">{n.text}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : <div className="text-[12px] text-[#0b1f22]/50 mb-4">No notes yet.</div>}
                        <div className="flex gap-2">
                            <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add an internal note…" data-testid="new-note-input" className={inputCls} />
                            <button onClick={addNote} data-testid="add-note" className="bg-[#0b1f22] text-ivory rounded-lg px-4 text-[11px] uppercase tracking-[0.18em] hover:bg-[#0b1f22]/90">Add</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} onSaved={(res) => { setDoc(res); setPayOpen(false); }} reservationId={id} remaining={doc.remaining_balance} />
            <RefundModal open={refundOpen} onClose={() => setRefundOpen(false)} onSaved={(res) => { setDoc(res); setRefundOpen(false); }} reservationId={id} amountPaid={doc.amount_paid} />
            <CancelModal open={cancelOpen} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); load(); }} reservationId={id} />
        </div>
    );
}

function PaymentModal({ open, onClose, onSaved, reservationId, remaining }) {
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("cash");
    const [reference, setReference] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setAmount(Number(remaining || 0).toFixed(2)); setMethod("cash"); setReference(""); setNote(""); } }, [open, remaining]);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await adminApi.post(`/admin/reservations/${reservationId}/payments`, {
                amount: Number(amount), method, reference: reference || null, note: note || null,
            });
            toast.success("Payment recorded");
            onSaved(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Modal open={open} onClose={onClose} title="Record payment">
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="payment-form">
                <Field label="Amount (USD)"><input type="number" step="0.01" required min="0.01" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" /></Field>
                <Field label="Method">
                    <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
                        <option value="cash">Cash</option><option value="paypal">PayPal (offline)</option>
                        <option value="card">Card</option><option value="bank_transfer">Bank transfer</option><option value="other">Other</option>
                    </select>
                </Field>
                <Field label="Reference / receipt #"><input className={inputCls} value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
                <Field label="Note (optional)"><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
                <button type="submit" disabled={busy} className="w-full bg-jade text-white rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-jade/90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    <CheckCircle2 size={13} /> {busy ? "Recording…" : "Record payment"}
                </button>
            </form>
        </Modal>
    );
}

function RefundModal({ open, onClose, onSaved, reservationId, amountPaid }) {
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setAmount(""); setReason(""); } }, [open]);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await adminApi.post(`/admin/reservations/${reservationId}/refunds`, { amount: Number(amount), reason: reason || null });
            toast.success("Refund recorded");
            onSaved(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Modal open={open} onClose={onClose} title="Issue refund">
            <p className="text-xs text-[#0b1f22]/55 mt-1">Refund up to {usd(amountPaid)} (amount previously paid). Actual PayPal refund is triggered from your PayPal dashboard — this just records it in the system.</p>
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="refund-form">
                <Field label="Refund amount (USD)"><input type="number" step="0.01" required min="0.01" max={amountPaid} className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" /></Field>
                <Field label="Reason"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Guest cancellation, goodwill" /></Field>
                <button type="submit" disabled={busy} className="w-full bg-[#0b1f22] text-ivory rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0b1f22]/90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    <RefreshCcw size={13} /> {busy ? "Recording…" : "Record refund"}
                </button>
            </form>
        </Modal>
    );
}

function CancelModal({ open, onClose, onDone, reservationId }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setReason(""); }, [open]);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await adminApi.post(`/admin/reservations/${reservationId}/status`, { status: "cancelled", reason: reason || null });
            toast.success("Reservation cancelled");
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Modal open={open} onClose={onClose} title="Cancel reservation">
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="cancel-form">
                <Field label="Reason (optional)"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Guest request, weather, no-show" /></Field>
                <button type="submit" disabled={busy} className="w-full bg-red-600 text-white rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-red-700 disabled:opacity-60">
                    {busy ? "Cancelling…" : "Cancel reservation"}
                </button>
            </form>
        </Modal>
    );
}
